<script>
import ProfileLayout from "@/Layouts/ProfileLayout.vue";

export default {
    layout: ProfileLayout,
};
</script>

<script setup>
import { computed } from "vue";
import { Head, Link, usePage } from "@inertiajs/vue3";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { LIST_SORT_OPTIONS, useListGrid } from "@/composables/useListGrid.js";
import FollowButton from "@/Components/Profile/FollowButton.vue";
import ListsPagination from "@/Components/Profile/ListsPagination.vue";
import ProfileHero from "@/Components/Profile/ProfileHero.vue";
import ProfileTabs from "@/Components/Profile/ProfileTabs.vue";
import PromoBanner from "@/Components/Profile/PromoBanner.vue";
import SortDropdown from "@/Components/Profile/SortDropdown.vue";
import WishListCard from "@/Components/Profile/WishListCard.vue";

// Access props through the page object so in-place reloads aren't read from
// a stale props reference.
const inertiaPage = usePage();
const authUser = useAuthUser();

const owner = computed(() => inertiaPage.props.owner?.data);

// One combined grid: expert lists carry their own badge (per Figma).
const allLists = computed(() => [
    ...(inertiaPage.props.expertlists?.data ?? []),
    ...(inertiaPage.props.giftlists?.data ?? []),
]);

const { sort, page, pages, paged } = useListGrid(allLists);

const ownerPossessive = computed(() =>
    /['’]s$/i.test(ownerShortName.value ?? "")
        ? ownerShortName.value
        : `${ownerShortName.value}’s`,
);

const ownerShortName = computed(() =>
    owner.value.is_organization
        ? owner.value.presentation_name
        : owner.value.first_name,
);

const ownerDisplayName = computed(() =>
    owner.value.is_organization
        ? owner.value.presentation_name
        : owner.value.full_name,
);

// Decided server-side, which also sends a matching X-Robots-Tag header.
const noIndex = computed(() => inertiaPage.props.noIndex !== false);
</script>

<template>
    <div>
        <Head>
            <title>{{ `${ownerDisplayName}’s Lists | Gift Hero` }}</title>
            <meta v-if="noIndex" name="robots" content="noindex, follow" />
        </Head>

        <ProfileHero
            :owner="owner"
            :is-owner="false"
            :theme="owner.profile_theme"
        />

        <ProfileTabs :owner="owner" :is-owner="false">
            <FollowButton
                v-if="authUser"
                :owner="owner"
                class="w-full max-lg:order-2 lg:hidden"
            />
        </ProfileTabs>

        <div class="px-4 pb-8 max-lg:bg-brand-cream sm:px-[35px] sm:pb-9">
            <section
                class="rounded-brand bg-brand-cream px-4 py-6 sm:px-[46px] sm:py-8"
            >
                <header
                    class="flex flex-col gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center"
                >
                    <p
                        v-if="!authUser"
                        class="max-w-[280px] font-brand text-[15px] font-semibold leading-[1.4] text-[#102a3d] max-lg:hidden"
                    >
                        Want to follow {{ ownerShortName }} or create your own
                        lists?
                        <Link
                            :href="route('register')"
                            class="text-brand-red underline underline-offset-2"
                            >Sign Up!</Link
                        >
                    </p>
                    <div v-else class="max-lg:hidden"></div>
                    <h2
                        class="text-center font-brand text-[25px] font-bold leading-none text-brand-navy lg:text-[34px]"
                    >
                        {{ ownerPossessive }} Lists
                    </h2>
                    <SortDropdown
                        v-model="sort"
                        :options="LIST_SORT_OPTIONS"
                        class="lg:justify-self-end"
                    />
                </header>

                <template v-if="allLists.length">
                    <div
                        class="mt-7 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
                    >
                        <WishListCard
                            v-for="list in paged"
                            :key="list.uuid"
                            :list="list"
                            :is-owner="false"
                            :owner-is-gift-hero="!!owner.is_gift_hero"
                        />
                    </div>

                    <ListsPagination
                        v-if="pages > 1"
                        v-model:page="page"
                        :pages="pages"
                        class="mt-10"
                    />
                </template>

                <div
                    v-else
                    class="flex flex-col items-center gap-2 py-14 text-center"
                >
                    <h3
                        class="font-brand text-[25px] font-bold text-brand-navy"
                    >
                        Awww&hellip;
                    </h3>
                    <p class="max-w-md font-brand text-[16px] text-[#565a77]">
                        {{ ownerShortName }} doesn&apos;t have any lists yet.
                        Check back!
                    </p>
                </div>
            </section>

            <!-- Guests get the account + explore pair; members get Giftspiration -->
            <div v-if="!authUser" class="mt-8 grid gap-6 lg:grid-cols-2">
                <PromoBanner
                    headline="The Ultimate
Gift Giver"
                    cta-label="Create an Account"
                    :cta-href="route('register')"
                    image="images/profile/promo-gift-giver.png"
                    variant="gold"
                    compact
                />
                <PromoBanner
                    headline="Need Some
Giftspiration?"
                    cta-label="Explore Gift Ideas"
                    :cta-href="route('explore')"
                    image="images/profile/cta-collage.png"
                    mobile-image="images/profile/cta-collage-mobile.png"
                    variant="purple"
                    compact
                />
            </div>
            <PromoBanner
                v-else
                class="mt-8"
                headline="Need Some
Giftspiration?"
                body="Browse hand-picked gift ideas from our experts and find something they’ll actually love — for any occasion, any budget."
                cta-label="View Expert Lists"
                :cta-href="route('explore')"
                image="images/profile/cta-collage.png"
                mobile-image="images/profile/cta-collage-mobile.png"
                variant="navy"
            />
        </div>
    </div>
</template>
